import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { track } from '@amplitude/analytics-browser';
import * as NotesActions from '../../stores/actions/notes';
import * as TelepromptersActions from '../../stores/actions/teleprompters';
import { GreenroomState as GS } from '../../stores/types/types';
import { TeleprompterInterface } from '../../types/interfaces';
import colors from '../../utils/colors';
import Socket from '../../connection/socket';
import { BestPractices } from './notes/best-practices';
import type { Note } from '../../stores/types/notes';
import { uniqueArray } from '../../utils/utils';
import Popover from '../general-components/popover';
import LoadingSpinner from '../general-components/loading-spinner';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import NoteLink from './notes/note-link';
import NoteEditor from './notes/note-editor';
import TeleprompterEditor from './teleprompter/teleprompter-editor';
import useTeleprompters from '../../hooks/use-teleprompters';
import { AmpEvent } from '../../types/amplitude';
import type { MediaItem } from '../../stores/types/media';

interface Props {
	socket: Socket | null;
	isForAllNotes: boolean;
}

function extractUserFromInvite(user: any): any {
	return user.profile;
}

// User-defined type guard
function isTeleprompter(arg: TeleprompterInterface | Note): arg is TeleprompterInterface {
	return (arg as TeleprompterInterface).sections !== undefined;
}

export default function NotesTeleprompterWrapper(props: Props) {
	const dispatch = useDispatch();
	const {
		socket,
		isForAllNotes,
	} = props;

	const {
		loadTeleprompters,
		createTeleprompter,
		handleSetMediaTeleprompter,
	} = useTeleprompters(socket);

	const user = useSelector((s: GS) => s.SessionReducer.user);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const selectedTeleprompters = useSelector((s: GS) => s.TeleprompterReducer.selectedTeleprompters);
	const showWorkingNote = useSelector((s: GS) => s.NotesReducer.showWorkingNote);
	const showWorkingAllNote = useSelector((s: GS) => s.NotesReducer.showWorkingAllNote);
	const workingNote = useSelector((s: GS) => s.NotesReducer.workingNote);
	const workingAllNote = useSelector((s: GS) => s.NotesReducer.workingAllNote);
	const notes = useSelector((s: GS) => s.NotesReducer.notes);
	const notesLoaded = useSelector((s: GS) => s.NotesReducer.notesLoaded);
	const teleprompters = useSelector((s: GS) => s.TeleprompterReducer.teleprompters);
	const telepromptersLoaded = useSelector((s: GS) => s.TeleprompterReducer.telepromptersLoaded);
	const connectedUsers = useSelector((s: GS) => s.SessionReducer.connectedUsers);
	const invitations = useSelector((s: GS) => s.InvitationsReducer.invitations);
	const media = useSelector((s: GS) => s.MediaReducer.media);

	const [onlyMine, setOnlyMine] = useState<boolean>(true);
	const [createNotePopoverOpen, setCreateNotePopoverOpen] = useState<boolean>(false);

	useEffect(() => {
		if (token) {
			dispatch(NotesActions.getNotes(token));
		}
	}, []);

	useEffect(() => {
		if (token && !telepromptersLoaded) {
			loadTeleprompters();
		}
	}, [token, telepromptersLoaded]);

	const goToNote = (note: Note | TeleprompterInterface) => {
		if (!isTeleprompter(note)) {
			track(AmpEvent.noteViewed);
			if (isForAllNotes) {
				dispatch(NotesActions.setWorkingAllNote(note));
				dispatch(NotesActions.setShowWorkingAllNote(true));
			} else {
				dispatch(NotesActions.setWorkingNote(note));
				dispatch(NotesActions.setShowWorkingNote(true));
			}
		} else {
			track(AmpEvent.shotNoteViewed);
			dispatch(TelepromptersActions.setSelectedTeleprompters(['shot', note.id]));
		}

		setCreateNotePopoverOpen(false);
	};

	const handleCreateNote = async () => {
		track(AmpEvent.noteCreated);
		if (token) {
			if (isForAllNotes) {
				await dispatch(NotesActions.createNote(token, meeting ? meeting.meeting : 0, true));
				dispatch(NotesActions.setShowWorkingAllNote(true));
			} else {
				await dispatch(NotesActions.createNote(token, meeting ? meeting.meeting : 0, false));
				dispatch(NotesActions.setShowWorkingNote(true));
			}
		}

		setCreateNotePopoverOpen(false);
	};

	const handleCreateTeleprompter = async () => {
		if (token && meeting && user) {
			const newTeleprompter = await createTeleprompter({
				id: 0,
				meeting: meeting?.meeting,
				gr_user: user.gr_user,
				title: '',
				sections: [],
				created_date: '',
				shared_with: [],
			});

			if (newTeleprompter) {
				dispatch(TelepromptersActions.setSelectedTeleprompters(['shot', newTeleprompter.id]));
				setCreateNotePopoverOpen(false);
			}
		}

		setCreateNotePopoverOpen(false);
	};

	const possibleUsers = useMemo(() => uniqueArray('gr_user', [
		...invitations.map(extractUserFromInvite),
		...connectedUsers,
	]), [connectedUsers, invitations]);

	const displayNotes = useMemo(() => {
		if (!isForAllNotes && meeting) {
			// On the notes tab, simple notes and teleprompters are combined in a list and sorted by date
			const notesAndTeleprompters = [
				...notes.filter((note: Note) => note.meetings?.includes(meeting.meeting)),
				...teleprompters.filter((teleprompter: TeleprompterInterface) => !teleprompter.media_id),
			].sort((a: any, b: any) => (a.edited_date < b.edited_date ? 1 : -1));

			return notesAndTeleprompters;
		}

		if (onlyMine) {
			// Decoupling migration strategy: Added "brandlive_profile"
			// for notes created pre-decoupling ("gr_user" didn't exist)
			return notes.filter(
				(note: Note) => note.created_by === user!.gr_user
				|| (user!.brandlive_profile && note.created_by === user!.brandlive_profile),
			);
		}
		return notes.filter((note: Note) => note.created_by !== user!.gr_user);
	}, [notes, onlyMine, user, meeting, isForAllNotes, teleprompters]);

	const slideNotes = useMemo(
		() => media
			.filter((mediaItem: MediaItem) => mediaItem.num_pages > 1)
			.map((mediaItem: MediaItem, index: number) => (
				<div className="padding-button-small-wide" key={index}>
					<button
						type="button"
						className="flex-align-center justify-content-space-between background-light background-highlight border-radius-12 padding-medium cursor-pointer width-full"
						style={{ textAlign: 'left' }}
						onClick={() => { handleSetMediaTeleprompter(mediaItem); }}
					>
						<div className="flex-column width-full overflow-hidden">
							<span className="text-gray font-size-12 font-weight-normal line-height-16 margin-bottom-extra-small">Slide notes</span>
							<span className="font-weight-heavy line-height-20 truncate-overflow">{mediaItem.custom_name || mediaItem.original_name}</span>
						</div>
						<Icon name={ICONS.ARROW_RIGHT} color={COLORS.LIGHT_GRAY} size={16} />
					</button>
				</div>
			)),
		[media, teleprompters],
	);

	return (
		!notesLoaded || !telepromptersLoaded ? (
			<div className="notes-loading-container">
				<LoadingSpinner
					height={100}
					width={100}
					color={colors.red}
				/>
			</div>
		) : (
			<>
				{isForAllNotes ? (
					<div className="notes-wrapper height-full load-all">
						{showWorkingAllNote && (
							<NoteEditor
								selectedNote={workingAllNote}
								isForAllNotes={true}
								socket={socket}
							/>
						)}

						{!showWorkingAllNote && (
							<div className="notes-container width-full">
								<div className={classNames('pill-tabs width-full', {
									'my-notes': onlyMine,
									'shared-with-me': !onlyMine,
								})}>
									<button className="my-notes" onClick={() => setOnlyMine(true)}>My Notes</button>
									<button className="shared-with-me" onClick={() => setOnlyMine(false)}>Shared Notes</button>
								</div>
								<div className="all-notes">
									<div className='tab-row'>
										<div className="create-note width-full" onClick={handleCreateNote}>
											<Icon className='margin-right-small' name={ICONS.ADD} color={colors.textGray} size={16} />
											<span>Create</span>
										</div>
									</div>
									<NoteLink
										onClick={() => goToNote(BestPractices)}
										note={BestPractices}
										users={[]}
									/>
									{displayNotes.map((note: Note | TeleprompterInterface, index: number) => (
										<NoteLink
											onClick={() => goToNote(note)}
											note={note}
											key={index}
											users={possibleUsers}
										/>
									))}
								</div>
							</div>
						)}
					</div>
				) : (
					<div className="notes-wrapper height-full">
						{(selectedTeleprompters.shot || selectedTeleprompters.media) && (
							<TeleprompterEditor
								socket={socket}
							/>
						)}

						{showWorkingNote && (
							<NoteEditor
								selectedNote={workingNote}
								isForAllNotes={false}
								socket={socket}
							/>
						)}

						{!(showWorkingNote || selectedTeleprompters.shot || selectedTeleprompters.media) && (
							<>
								<div className="notes-container width-full">
									<NoteLink
										onClick={() => goToNote(BestPractices)}
										note={BestPractices}
										users={[]}
									/>
									{displayNotes.map((note: Note | TeleprompterInterface, index: number) => (
										<NoteLink
											onClick={() => goToNote(note)}
											note={note}
											key={index}
											users={possibleUsers}
										/>
									))}
									{slideNotes}
								</div>
								<div className="create-note-wrapper width-full" >
									<div className="flex-center background-lighter border-radius-100">
										<div className="flex-center padding-medium width-full cursor-pointer" onMouseDown={() => setCreateNotePopoverOpen(true)}>
											<span className="font-weight-heavy margin-right-small">Create Note</span>
											<span>
												<Icon
													name={
														createNotePopoverOpen
															? ICONS.KEYBOARD_ARROW_DOWN
															: ICONS.KEYBOARD_ARROW_UP
													}
													color={COLORS.WHITE}
													size={12}
												/>
											</span>
										</div>
										<Popover
											open={createNotePopoverOpen}
											close={() => setCreateNotePopoverOpen(false)}
											className='options'
										>
											<>
												<button className="option background-light" onClick={handleCreateNote}>
													<Icon className='margin-right-small' name={ICONS.NOTE} color={COLORS.WHITE} size={13} />
													<span className="text-white font-weight-normal line-height-20">Note</span>
												</button>
												<button className="option background-light" onClick={handleCreateTeleprompter}>
													<Icon className='margin-right-small' name={ICONS.TELEPROMPTER} color={COLORS.WHITE} size={13} />
													<span className="text-white font-weight-normal line-height-20">Shot Notes</span>
												</button>
											</>
										</Popover>
									</div>
								</div>
							</>
						)}
					</div>
				)}
			</>
		)
	);
}
